import { useLoadContext } from "../contextLib/contextLib"
import { clientApi, getUsers, updateUser, getSSORedirectLink, trackPlanhat, getMigrationUrl } from "../apiLib/apiLib"
import { Auth } from "@aws-amplify/auth"
import type { Client, Team, Company, UserFromDb, ClientOptions } from "shared/src/DatabaseObjects/DatabaseObjects"
import { RESTRICTABLE_FEATURES } from "shared/src/libs/featuresLib/restrictableFeatures"
import type { ClientProductionFeaturePermissionDefinition } from "shared/src/libs/featuresLib/types"
import type { ReactNode } from "react"

declare global {
	var Intercom: any
	var wootric: any
}

interface Attributes {
	email: string
	name: string
	jobTitle?: string
	phone?: string
	linkedInUrl?: string
	displayName?: string
	displayCompanyName?: string
	"custom:companyId": string
	"custom:company": string
	"custom:subdomain": string
	"custom:userrole": string
}

interface UserAttributesForRegistration {
	flags: ClientOptions
	companyName: string
	companyId: string
	userRole: string
	userEmail: string
	userName: string
	userId: string
}

export interface Response {
	username: string
	attributes: Attributes
	challengeParam?: {
		userAttributes: Attributes
	}
	authenticationFlowType?: string
}

interface Session {
	idToken: {
		jwtToken: string
	}
}

interface LocalUserDetails {
	isAuthenticated: boolean
	name: string
	email: string
	id: string
	role: string
	phone?: string
	jobTitle?: string
	createdAt?: number
	linkedInUrl?: string
	displayName?: string
	displayCompanyName?: string
	calendarLinkUrl?: string
	permissions: { [key: string]: boolean }
	betaPermissions: { [key: string]: boolean }
	ssoLogin: boolean
	evoEnabled?: boolean
}

export function setupIntercomUser({
	flags,
	companyName,
	companyId,
	userEmail,
	userName,
	userRole,
	userId,
}: UserAttributesForRegistration) {
	if (userEmail == null || userEmail.toLowerCase() === "no email") {
		return
	}
	const isAdmin = userRole === "admin"
	const userFromIntegration = isUserFromIntegration(userId)
	const flagsString = getIntercomOptionsString(flags)

	window.Intercom?.("boot", {
		api_base: "https://api-iam.intercom.io",
		app_id: process.env.VITE_IS_DEV === "true" || process.env.VITE_IS_TEST === "true" ? "l3ib09kz" : "v4dc86p5",
		name: userName,
		email: userEmail,
		company: {
			id: companyId,
			name: companyName,
			"enabled options": flagsString,
		},
		userRole: userRole,
		is_admin: isAdmin,
		is_user_from_integration: userFromIntegration,
	})
}

export async function setupPlanhatUser({ companyId, userEmail, userName }: Partial<UserAttributesForRegistration>) {
	try {
		await trackPlanhat(
			{
				email: userEmail,
				name: userName,
				action: "logged in",
			},
			companyId
		)
	} catch (error) {
		console.warn("Error tracking user login to Planhat:", error)
	}
}

function getIntercomOptionsString(flags: ClientOptions) {
	const optionsForIntercom = Object.entries(RESTRICTABLE_FEATURES).map(([key, value]) => ({
		key,
		value: flags[key] ?? (value as ClientProductionFeaturePermissionDefinition).defaultClientAccess,
	}))

	return optionsForIntercom
		.filter((option) => option.value)
		.map((option) => option.key)
		.join(",")
}

function isUserFromIntegration(userId: string) {
	return userId?.length !== 36
}

/**
 *
 * @param userObj the response from a call to the database to grab user data
 * @param response  the response from a call to cognito or data stripped from a jwt
 * @param attributes the attributes from a sign up sequence, should be equivalent to the response.attributes
 * @returns user details for local use
 */
export function makeUser(
	userObj: UserFromDb | null,
	response: Response,
	attributes: Partial<Attributes>
): LocalUserDetails {
	attributes = attributes?.name ? attributes : (response.attributes ?? {})
	let ssoLogin = localStorage.getItem("amplify-signin-with-hostedUI") === "true"
	if (attributes?.email?.toLowerCase() === "no email") {
		// There should be no corresponding user in the database so use attributes directly to avoid unexpected behaviour
		return {
			isAuthenticated: true,
			name: attributes?.name,
			email: attributes?.email,
			id: response?.username,
			role: attributes?.["custom:userrole"],
			phone: attributes?.phone,
			jobTitle: attributes?.jobTitle,
			linkedInUrl: attributes?.linkedInUrl,
			displayName: attributes?.displayName,
			displayCompanyName: attributes?.displayCompanyName,
			calendarLinkUrl: userObj?.calendarLinkUrl,
			permissions: {},
			betaPermissions: {},
			ssoLogin: ssoLogin,
			evoEnabled: true,
		}
	}

	return {
		isAuthenticated: true,
		name: userObj?.name ?? attributes.name,
		email: userObj?.miscData ? userObj?.miscData.split("#")[1] : attributes.email,
		id: userObj?.sk ? userObj?.sk.split("#")[1] : response.username,
		role: userObj?.userRole ?? attributes["custom:userrole"],
		phone: userObj?.phone ?? attributes.phone,
		jobTitle: userObj?.jobTitle ?? attributes.jobTitle,
		createdAt: Number(userObj?.createdAt) || Math.floor(Date.now() / 1000),
		linkedInUrl: userObj?.linkedInUrl ?? attributes.linkedInUrl,
		displayName: userObj?.displayName ?? attributes.displayName,
		displayCompanyName: userObj?.displayCompanyName ?? attributes.displayCompanyName,
		calendarLinkUrl: userObj?.calendarLinkUrl,
		permissions: userObj?.permissions ?? {},
		betaPermissions: userObj?.betaPermissions ?? {},
		ssoLogin: ssoLogin,
		evoEnabled: userObj?.evoRef != null,
	}
}

export function makeCompany(attributes: Attributes, team: Team, numberRoles: number, clientObj: Client): Company {
	const stats = clientObj.stats
	const font = clientObj.font
	const options = clientObj.options
	const limits = clientObj.limits
	const branding = { font, ...clientObj.branding }
	const brandColors = clientObj.brandColors
	const socialLinks = clientObj.socialLinks
	const logoName = clientObj?.logos?.current
	const ssoEnabled = clientObj.ssoEnabled ?? false
	const evoOrgId = clientObj?.evoOrgId
	const evoEnabled = evoOrgId && ssoEnabled ? true : false
	return {
		id: clientObj.pk ? clientObj.pk.split("#")[1] : attributes["custom:companyId"],
		name: clientObj.name ?? attributes?.["custom:company"],
		subdomain: clientObj.subdomain ?? attributes?.["custom:subdomain"],
		logoName,
		team,
		stats,
		numberRoles,
		branding,
		brandColors,
		socialLinks,
		options,
		limits,
		ssoEnabled,
		evoEnabled,
	}
}

interface PostLoginProps {
	setUser: (user: any) => void
	setCompany: (company: any) => void
	response: Response
	unconfirmed?: boolean
	defaultTeam?: Team
	sessionOverride?: Session
	responseOnly?: boolean
	showAlert?: (
		message: string,
		title: string,
		confirmButtonText?: string,
		cancelButtontext?: string
	) => Promise<boolean>
}

async function getDatabaseItems(response?: Response, attributes?: Attributes, sessionOverride?: Session) {
	return (await Promise.all([
		clientApi.get({
			clientId: attributes?.["custom:companyId"],
			sessionOverride,
		}),
		response.username == null
			? null
			: getUsers({ clientId: attributes?.["custom:companyId"], userId: response.username }).catch((e) => null),
	])) as [Client, UserFromDb]
}

export function shouldRedirectToEvoMigration(company: Company, user: LocalUserDetails) {
	if (company.evoEnabled && !user.evoEnabled) {
		// company is in evo, but user is not yet migrated and is not integration user, show wizard
		return true
	}
	// otherwise no redirect needed, sso login redirect will prevent direct login in future
	return false
}

export async function evoMigrationRedirect({
	ssoEnabled,
	userId,
	companyId,
	showAlert,
}: {
	ssoEnabled: boolean
	userId: string
	companyId: string
	showAlert: (
		message: string | ReactNode,
		title: string | ReactNode,
		confirmButtonText?: string,
		cancelButtontext?: string
	) => Promise<boolean>
}) {
	if (isUserFromIntegration(userId)) {
		// do not allow evo migration for integration users, as they will migrate with their integration
		return
	}

	// get evo migration url
	const evoMigrationUrlPromise = getMigrationUrl(companyId, {
		userId: userId,
	})
	// show alert to inform user of redirect
	try {
		await showAlert(
			<>
				<p>Congratulations, your account has been upgraded to Access Evo.</p>
				<p>You will be redirected to the Evo setup page to connect your account to Evo.</p>
				{ssoEnabled ? <p>Please use your existing credentials to log in.</p> : <></>}
			</>,
			"Evo Upgrade",
			"Continue"
		)
	} catch {
		return
	}
	if (!ssoEnabled) {
		// sign out user to ensure SSO login is used after migration
		await Auth.signOut({ global: true })
	}
	// redirect to evo migration url
	const evoMigrationUrlResponse = await evoMigrationUrlPromise
	window.location.href = evoMigrationUrlResponse.migrationUrl
}

function shouldRedirectToSsoLogin(company: Company, response) {
	if (company.ssoEnabled && localStorage.getItem("amplify-signin-with-hostedUI") === "false") {
		return true
	}

	return false
}

export function registerLoginToDatabase({
	companyId,
	username,
	user,
	accessIdToken,
}: {
	companyId: string
	username: string
	user: Pick<LocalUserDetails, "email" | "id" | "name" | "role">
	accessIdToken?: string
}) {
	if (username != null && user.email?.toLowerCase() !== "no email") {
		// user id given, register login
		console.log("register login")
		updateUser({
			email: user.email,
			userId: user.id,
			name: user.name,
			clientId: companyId,
			userRole: user.role,
			accessIdToken,
		})
	}
}

function extractRegistrationInfo(company: Company, user: LocalUserDetails): UserAttributesForRegistration {
	return {
		companyName: company.name,
		companyId: company.id,
		userRole: user.role,
		userEmail: user.email,
		userName: user.name,
		userId: user.id,
		flags: company.options?.flags || {},
	}
}

export function allUserRegistrations(registrationData: UserAttributesForRegistration) {
	setupIntercomUser(registrationData)
	setupPlanhatUser(registrationData)
}

/**
 * Post login does the user/company setup after the user has logged in
 * Uses a combination of the attirbutes returned from cognito and items loaded from the database
 */
export async function postLogin({
	setUser,
	setCompany,
	response,
	unconfirmed = false,
	defaultTeam = {},
	sessionOverride,
	responseOnly,
	showAlert,
}: PostLoginProps) {
	const attributes = unconfirmed ? response?.challengeParam?.userAttributes : response?.attributes
	const [clientObj, userObj]: [Client, UserFromDb | null] = await getDatabaseItems(
		response,
		attributes,
		sessionOverride
	)
	// Set company data
	const numberRoles = parseInt((clientObj.miscData || " #0").split("#")[1])
	const company = makeCompany(attributes, clientObj.userlist ?? defaultTeam, numberRoles, clientObj)
	setCompany(company)

	const user = makeUser(userObj, response, attributes)

	if (!responseOnly && shouldRedirectToEvoMigration(company, user)) {
		// display explainer popover then redirect to evo migration page by calling api to generate link
		await evoMigrationRedirect({
			ssoEnabled: false, // only non sso users can reach this point
			userId: user.id,
			companyId: company.id,
			showAlert,
		})
		return
	}

	if (!responseOnly && shouldRedirectToSsoLogin(company, response)) {
		const link = await getSSORedirectLink({ clientId: attributes?.["custom:companyId"], userId: response.username })
		await Auth.signOut({ global: true })
		window.location.href = link.link
		return
	}
	// set user data
	setUser(user)
	// register last login in database
	registerLoginToDatabase({
		companyId: company.id,
		username: response.username,
		user,
		accessIdToken: attributes?.["custom:access_id_token"],
	})
	// register user in all third parties
	const registrationData = extractRegistrationInfo(company, user)
	allUserRegistrations(registrationData)
}

interface LoginProps {
	responseOnly?: boolean
	setCompanyOverride?: (company: any) => void
	setUserOverride?: (user: any) => void
	unconfirmed?: boolean
	showAlert?: (
		message: string | ReactNode,
		title: string | ReactNode,
		confirmButtonText?: string,
		cancelButtontext?: string
	) => Promise<boolean>
}

// define overloading for different return types
export function useLogin(
	props: LoginProps & { responseOnly?: true }
): (props: { response: Response; sessionOverride?: Session }) => Promise<void>
export function useLogin(
	props: LoginProps & { responseOnly?: false }
): (props: {
	email: string
	password: string
	defaultTeam?: Team
	validationData: { [key: string]: string }
	sessionOverride?: Session
}) => Promise<void>
/**
 * useLogin is a convenience hook so that the setUser and setCompany functions are automatically loaded from the context and the post login function is called with parameters set automatically
 * @param param0
 * @returns
 */
export function useLogin({
	responseOnly = false,
	setCompanyOverride,
	setUserOverride,
	unconfirmed,
	showAlert = async () => true, // Default no-op that always returns true
}: LoginProps) {
	const { setUser, setCompany } = useLoadContext()
	//Overriding is allowed in order to use the setters in the initial call before context is set
	const setUserFunc = setUserOverride || setUser
	const setCompanyFunc = setCompanyOverride || setCompany
	if (responseOnly) {
		return async ({ response, sessionOverride = undefined }) => {
			await postLogin({
				setUser: setUserFunc,
				setCompany: setCompanyFunc,
				response,
				unconfirmed,
				sessionOverride,
				responseOnly,
				showAlert,
			})
		}
	} else {
		return async ({
			email,
			password,
			defaultTeam,
			validationData,
			sessionOverride,
		}: {
			email: string
			password: string
			defaultTeam: Team
			validationData: { [key: string]: string }
			sessionOverride: Session
		}) => {
			const response = await Auth.signIn(email, password, validationData)
			await postLogin({
				setUser: setUserFunc,
				setCompany: setCompanyFunc,
				response,
				unconfirmed,
				defaultTeam,
				sessionOverride,
				responseOnly,
				showAlert,
			})
		}
	}
}

/**
 * Takes the current user's id and name, then places it in the same format as a user in the company.team object
 * @param {string} currUserId The current user's id
 * @param {string} currName The current user's name
 */
export function makeTeamFromCurrentUser(currUserId: string, currName: string, email: string): Team {
	return { [currUserId]: { name: currName, user_role: "admin", email } }
}
